/**
 * @fileoverview Provides the `askQuestion` function to query a codebase using AI, leveraging context providers like `thinkAndGetContext` and `embeddingContextProvider` for enhanced understanding. It uses `streamChatCompletions` to generate a streaming response via the `AiStreamer` class, supporting different language models. The function returns the AI-generated answer as a string.
 */

import { Model } from '@iterator/shared-node/ai/llm_types'
import {
  error,
  info_block,
  isVerbose,
  print_ai,
  print_ai_stream,
  print_ai_stream_code,
} from '@iterator/shared-node/util/logging'
import { printTiming, time } from '@iterator/shared-node/util/timing'
import { SingleTonUsageTracker } from '@iterator/shared-node/util/usage_tracker'
import { streamChatCompletions } from './ai/llm_api'
import { AiStreamer } from './ai_streamer'
import { ContextProviders, embeddingContextProvider, thinkAndGetContext } from './context'
import { ContextProviderFn } from './context_types'
import { SparsePath } from '@iterator/shared-node/util/sparse_path'
import { getMarkdownFromUrl } from './web'

const SYSTEM_PREFIX = `You are an expert coding assistant answering questions about a codebase. You should:
1. Provide clear, concise answers
2. Reference specific files and line numbers when relevant
3. Include code snippets to illustrate your points.
4. When outputing a code snippet, include the entire file path.
5. Acknowledge if you're unsure about any part of your answer`

/**
 * Ask a question about the codebase and get an AI-generated response
 */
export async function askQuestion(
  directory: SparsePath,
  question: string,
  model: Model,
  contextProviderName: keyof typeof ContextProviders,
  docs?: string,
  url?: string,
): Promise<string> {
  const {
    system,
    texts,
    timing: contextTiming,
  } = await time('getContext', async () => {
    const context = await thinkAndGetContext(
      directory,
      question,
      true,
      ContextProviders[contextProviderName] as ContextProviderFn,
    )

    let combinedSystem = context.system
    let combinedTexts = context.texts

    if (url) {
      const webContent = await getMarkdownFromUrl(url)
      combinedTexts = [
        ...combinedTexts,
        `<web_content source="${url}">\n${webContent}\n</web_content>`,
      ]
    }

    if (docs) {
      const docContext = await embeddingContextProvider(docs, question)
      info_block('Documentation context files', docContext.system)
      combinedSystem = [combinedSystem, docContext.system].join('\n')
      combinedTexts = [...combinedTexts, ...docContext.texts]
    }

    return {
      system: combinedSystem,
      texts: combinedTexts,
    }
  })

  const streamer = new AiStreamer(print_ai_stream, print_ai_stream_code)
  const { message, timing: streamTiming } = await time('streamCompletion', async () =>
    streamChatCompletions(
      `${SYSTEM_PREFIX}\n\n${system}`,
      texts,
      [{ role: 'user', content: question }],
      (chunk) => {
        if (!isVerbose()) {
          streamer.stream(chunk)
        }
      },
      model,
    ),
  )

  if (isVerbose()) {
    streamer.stream(message.content)
  }

  print_ai('')
  printTiming('ask', [contextTiming, streamTiming])
  SingleTonUsageTracker.log('ask')

  if (!message.content.trim()) {
    error('No response received from AI')
  }

  return message.content
}
